import { Accumulation } from '@sentara-health/sentara-api-hooks-core/dist/typings/models/ciamMember';

interface BenefitLimit {
  name: string;
  amount: string;
  limit: string;
}

interface OutputBenefitData {
  benefitDescription: string;
  networkType: string;
  family?: BenefitLimit;
  member?: BenefitLimit;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  // Optional: keep if you ever get bigint
  if (typeof value === 'bigint') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  if (typeof value !== 'string') return null;

  const str = value.trim();
  if (!str) return null;

  // Remove commas, $, spaces, etc. Keep digits, minus, dot.
  const cleaned = str.replaceAll(/[^\d.-]/g, '');
  if (!cleaned) return null;

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const isZeroOrEmpty = (value: unknown): boolean => {
  const n = toNumber(value);
  return n === null || n === 0;
};

const formatAmount = (value: unknown): string => {
  const n = toNumber(value);
  if (n === null) return '$0';

  // Assuming you want whole dollars (your original code floors)
  const whole = Math.floor(n);
  return `$${whole.toLocaleString('en-US')}`;
};

const mapBenefitDescription = (
  collectionHeader: string | undefined
): string => {
  switch (collectionHeader) {
    case 'DEDUCTIBLE':
      return 'DEDUCTIBLE';
    case 'MAXOUT':
    case 'IN NETWORK MOOP':
      return 'IN NETWORK MOOP';
    default:
      return collectionHeader ?? '';
  }
};

export const transformOverviewData = (
  inputData: Accumulation[]
): OutputBenefitData[] => {
  if (!Array.isArray(inputData) || inputData.length === 0) return [];

  // If you only ever output In Network, filter early to reduce noise
  const inNetwork = inputData.filter(
    (x) =>
      x?.netWork === 'I' &&
      (x.collectionHeader === 'DEDUCTIBLE' ||
        x.collectionHeader === 'IN NETWORK MOOP' ||
        x.collectionHeader === 'MAXOUT')
  );

  // Group only by collectionHeader now (network is already filtered)
  const grouped = inNetwork.reduce<Map<string, Accumulation[]>>((acc, item) => {
    const header = item.collectionHeader ?? '';
    const list = acc.get(header);
    if (list) list.push(item);
    else acc.set(header, [item]);
    return acc;
  }, new Map());

  const result: OutputBenefitData[] = [];

  const getPriority = (desc: string | undefined): number => {
    if (!desc) return 999;
    const upper = desc.toUpperCase();
    if (upper.includes('IN NETWORK')) return 1;
    if (upper.includes('TIER 2')) return 2;
    return 3;
  };

  for (const [collectionHeader, items] of Array.from(grouped.entries())) {
    let memberData: Accumulation | undefined;
    let familyData: Accumulation | undefined;
    let minPriority = Infinity;

    // Single pass: find both member and family data with highest priority
    for (const item of items) {
      const priority = getPriority(item.benefitDescription);

      if (!memberData && !isZeroOrEmpty(item?.memberLimit)) {
        memberData = item;
        minPriority = priority;
      } else if (
        memberData &&
        priority < minPriority &&
        !isZeroOrEmpty(item?.memberLimit)
      ) {
        memberData = item;
        minPriority = priority;
      }

      if (!familyData && !isZeroOrEmpty(item?.familyLimit)) {
        familyData = item;
      } else if (
        familyData &&
        priority < getPriority(familyData.benefitDescription) &&
        !isZeroOrEmpty(item?.familyLimit)
      ) {
        familyData = item;
      }
    }

    if (!memberData || !familyData) continue;

    result.push({
      benefitDescription: mapBenefitDescription(collectionHeader),
      networkType: 'medicalClaims.inNetwork', 
      family: {
        name: 'yourPlan.family', 
        amount: formatAmount(familyData.familyAmount),
        limit: formatAmount(familyData.familyLimit),
      },
      member: {
        name: 'yourPlan.member',
        amount: formatAmount(memberData.memberAmount),
        limit: formatAmount(memberData.memberLimit),
      },
    });
  }

  return result;
};

export type { OutputBenefitData, BenefitLimit };